import {action, classNames, React, useLocalStore, observer, Fragment} from '@api'
import {Icon, Menu} from '@blueprintjs/core'
import {Popover2, Tooltip2} from '@api'
import {Classes} from '@blueprintjs/popover2'
import {Slider, Card, Dialog, Divider, TextField, Tooltip} from '@material-ui/core'
import {ContextMenuItem, HeroCardsToolbarButton, PdfMenuButton, PresentationModeButton, ShowGlobalAggregationsToggleButton, SwitchToPdfMenuItem, useStores, useVipLocalStore, VipLocalStore} from '@modules'
import {StoresInstance, useCatalog} from '@stores'
import {icons, PdfSvgIcon} from '@stores/ui'
import {checkIntegersOnly} from '@utils'
import {PrimaryIconButton, PrimaryIconButtonProps, SecondaryButton} from 'centric-ui-components'
import {observable} from 'mobx'
import {useEffect} from '@api'
import * as css from './CatalogActionButtons.css'

/**
 * Can be in the top page toolbar or in an individual tab's toolbar
 */
export const CatalogViewActionButtons = observer<{ isInTopBar?: boolean }>(function CatalogViewActionButtons({isInTopBar}) {
  const {
    catalogTab,
    catalog: {isActionButtonEnabled, canCreateItem, isClientPdfAvailable, isShowingAssortment, assortments}
  } = useStores()

  if (!catalogTab) return null

  return (
    <div className={classNames(css.root)}>

         {!assortments?.hideCatalogSpecificWidgets && canCreateItem && <AddCatalogItemButton/>}
         {false && isShowingAssortment && <HeroCardsToolbarButton/>}
         {!assortments?.hideCatalogSpecificWidgets && isInTopBar && <SidebarModalTopBarIcon/>}
         <ShowGlobalAggregationsToggleButton/>
      {isClientPdfAvailable && <SwitchToPDFToolbarButton/>}
      {/*<AddCatalogItemsButton/>*/}
         {/*<AddCatalogItemsWithImageButton/>*/}
         {!assortments?.hideCatalogSpecificWidgets && <SwitchCatalogViewDropdownButton/>}

      {!isClientPdfAvailable && <GenerateCatalogPDFToolbarButton />}
         <PresentationModeButton/>

    </div>
  )
})

export const GenerateCatalogPDFToolbarButton = observer(function GenerateCatalogPDFToolbarButton() {
  const {catalog: {pdfCatalogStore: {isServerPdfAvailable}}, cloudPDF: {defaultTemplate, prepareGenerateCatalogPDF}} = useStores()

  return !isServerPdfAvailable ? null : <PrimaryIconButton
    wrapperClassName={css.generatePdfButton}
      tooltip={'Generate PDF'}
      tooltipStyle='mui'
      disabled={!defaultTemplate || defaultTemplate.isDisabled}
      onClick={() => prepareGenerateCatalogPDF()}
      icon={<PdfSvgIcon/>}
    />
})

export const SwitchToPDFToolbarButton = observer(function SwitchToPDFToolbarButton() {
  const {catalog: {isShowingClientPDF, pdfCatalogStore: {toggleShowPdf}, isClientPdfAvailable, onSwitchToPdfView, onSwitchToCardView}, flexLayout: {isMaximized}} = useStores()

  if (!isClientPdfAvailable) return null

  return isMaximized
         ? <PdfMenuButton/>
         : (
           <PrimaryIconButton
               tooltip={'PDF'}
               active={isShowingClientPDF}
               onClick={toggleShowPdf}
               icon={<PdfSvgIcon/>}
             />
         )
})

export const GeneratePdfFromCatalogViewMenuItem = observer(function GeneratePdfFromCatalogViewMenuItem() {
    const {cloudPDF} = useStores()

  return <ContextMenuItem
    text={'Generate PDF from Catalog...'}
    icon={'cloud-upload'}
    onClick={() => cloudPDF.generateCatalogPDF()}/>
})

const OpenPdfInNewTabMenuItem = observer(function OpenPdfInNewTabMenuItem() {
  const {catalog: {isShowingClientPDF, openPDFInTab}, flexLayout: {activeTabId, isMaximized}} = useStores()
  return <ContextMenuItem
           text={'Open PDF In New Tab'}
           icon={'document-share'}
           onClick={openPDFInTab}/>
})

const OpenPDFInNewTab = observer(function OpenPDFInNewTab() {
  const {catalog: {isShowingClientPDF, openPDFInTab}, flexLayout: {activeTabId, isMaximized}} = useStores()
  return isMaximized
         ? <PdfMenuButton/>
         : <PrimaryIconButton
           tooltip={isShowingClientPDF ? 'Change Tab to Card View' : 'Open PDF In New Tab'}
           tooltipStyle='mui'
           active={isShowingClientPDF} icon={isShowingClientPDF ? <PdfSvgIcon/> : 'document-share'} onClick={openPDFInTab}/>
})

class CreateSliderStore extends VipLocalStore {
  constructor(stores: StoresInstance) {
    super(stores)
  }

  @observable count: number = 1
  constants = {
    MIN: 1,
    MAX: 10,
    STEP: 1
  }

  @action onChangeSlider = (event, count) => {
    this.count = count
  }

  @action onChangeSliderText = (e) => {
    let count = parseInt(e.target.value)
    if (!count) {
      count = 0
    }
    if (count < 1000 && count > -1) {
      this.count = count
    }
  }

  @action createMultipleItems = () => {
    const {count, catalog, catalog: {createItem: {createMultipleOfflineItemsByCount}}} = this

    createMultipleOfflineItemsByCount({count})
    catalog.createSlider = false
  }
  @action onDialogExit = () => {
    this.count = 1
  }
}

export const CreateSlider = observer(function CreateSlider() {
  const store = useVipLocalStore((s) => new CreateSliderStore(s)),
    {strings, constants: {MIN, MAX, STEP}, onChangeSlider, onChangeSliderText, createMultipleItems, count, onDialogExit, catalog, catalog: {createSlider, ref}} = store
  const classes: any = {}
  useEffect(() => {
    ref && ref?.scrollIntoViewIfNeeded(true)
  }, [ref, createSlider])

  return <Dialog
    className={css.createSlider}
    open={createSlider}
    aria-labelledby='create-catalog-item'
    aria-describedby='create-catalog-item-description'
    TransitionProps={{onExit: onDialogExit}}
  >
    <div className={css.header}>{`${strings.addProducts(catalog)}`}</div>
    <Divider/>
    <div className={css.content}>
      <div className={css.slider}>
        <Slider
          value={count}
          min={MIN}
          max={MAX}
          step={STEP}
          onChange={onChangeSlider}
          classes={{thumb: css.thumb, track: css.thumb}}
        />
      </div>
      <div className={css.text}>
        <TextField
          id='standard-name'
          className={classes.textField}
          value={count}
          onChange={onChangeSliderText}
          margin='normal'
          onKeyPress={checkIntegersOnly}
          inputProps={{maxLength: 3}}
          onPaste={checkIntegersOnly}
        />
      </div>
    </div>
    <Divider/>
    <div className={css.action}>
      <SecondaryButton onClick={() => catalog.createSlider = false}>Cancel</SecondaryButton>
      <SecondaryButton disabled={false} onClick={createMultipleItems}>Create</SecondaryButton>
    </div>
  </Dialog>
})

export class AddCatalogItemButtonStore extends VipLocalStore {
  @observable hover: boolean = false
}

export const SidebarModalTopBarIcon = observer(function SidebarModalTopBarIcon() {
  const {strings, settings: {detailLoc}, toggleDetailViewLocation, stores: {isInlineDetailsStore}} = useCatalog(),
    active = detailLoc === 'sidebar'

  if (isInlineDetailsStore) return null

  // DOn't show it in the sidebar header and the top toolbar simultaneously
  //if (active && sidebar.right.open) return null

  return (
    <PrimaryIconButton
      tooltip={!active ? strings.showDetailsInSidebar : strings.showInModal}
      tooltipStyle='mui'
      icon={<Icon icon='panel-stats'/>}
      active={active}
      onClick={toggleDetailViewLocation}
    />
  )
})

export const DetailViewLocationIconButton = observer(function DetailViewLocationIconButton() {
  const catalog = useCatalog(),
    inSidebar = catalog.settings.detailLoc === 'sidebar',
    {stores: {isInlineDetailsStore}, strings} = catalog

  if (isInlineDetailsStore) return null // YFTS-9933

  return (
    <PrimaryIconButton
      tooltip={inSidebar ? strings.showInModal : strings.showDetailsInSidebar}
      tooltipStyle='mui'
      icon={<Icon icon={icons.switchDetailViewLocation}/>}
      onClick={catalog.toggleDetailViewLocation}
    />
  )
})

const AddCatalogItemButton = observer<PrimaryIconButtonProps>(function AddCatalogItemButton(props) {
  const store = useVipLocalStore((s) => new AddCatalogItemButtonStore(s)),
    {hover} = store,
    {strings, catalog, catalog: {onSetRef, createItem, isShowingPerspective}, stores: {ui: {constants}}} = store

  if (!createItem) return null

  const {onCreateItemFromImages, onAddItemImageInputChanged, onSetAddCatalogItemInputRef, onCreateItem, onCreateItems} = createItem

  return (
    <Fragment>
      <Popover2 className={css.addButton} data-type='AddCatalogItemButton'
                interactionKind='hover'
                minimal
                hoverOpenDelay={0}
                usePortal={false}
                lazy={false}
                position='bottom'
                popoverClassName={css.addButtonPopover}
                content={<div className={css.addButtonsPanel}>
                  <PrimaryIconButton
                    wrapperClassName={Classes.POPOVER2_DISMISS}
                    tooltip={!isShowingPerspective ? strings.addProduct(catalog) : strings.cannotAddProduct_Perspective(catalog)}
                    onClick={onCreateItem}
                    {...props}
                    disabled={isShowingPerspective}
                  >
                    {icons.add}
                  </PrimaryIconButton>

                  <Tooltip key='add-products' placement={'left'}
                           className={css.addOfflineItem} data-display={hover}
                           title={!isShowingPerspective ? strings.addProducts(catalog) : strings.cannotAddProduct_Perspective(catalog)}>
                    <PrimaryIconButton
                      onClick={onCreateItems}
                      wrapperClassName={Classes.POPOVER2_DISMISS}
                      {...props}
                      disabled={isShowingPerspective}
                    >
                      {icons.addMany}
                    </PrimaryIconButton>
                  </Tooltip>
                  {/* Removed in YFTS-8384
        {/*<Tooltip2 key='add-product-image' placement={'left'}*/}
                  {/*         title={!isShowingPerspective ? strings.addProductWithImage : strings.cannotAddProduct_Perspective}*/}
                  {/*          className={css.actionButton}*/}
                  {/*>*/}
                  {/*  <PrimaryIconButton*/}
                  {/*    onClick={(e) => {*/}
                  {/*      onCreateItemFromImages()*/}
                  {/*    }}*/}
                  {/*    {...props}*/}
                  {/*    disabled={isShowingPerspective}*/}
                  {/*  >*/}
                  {/*    {icons.addFromImage}*/}
                  {/*  </PrimaryIconButton>*/}
                  {/*</Tooltip2>*/}
                  <Tooltip key='add-products-images' placement={'left'}
                           className={css.addOfflineItem} data-display={hover}
                           title={`${!isShowingPerspective ? strings.addProductsWithImages(catalog) : strings.cannotAddProduct_Perspective(catalog)}...`}>
                    <PrimaryIconButton
                      wrapperClassName={Classes.POPOVER2_DISMISS}
                      onClick={(e) => {
                        onCreateItemFromImages(true)
                      }}
                      {...props}
                      disabled={isShowingPerspective}
                    >
                      {icons.addFromImages}
                    </PrimaryIconButton>
                  </Tooltip>
                </div>}
      >
        <Tooltip key='add-product' placement={'left'}
                 title={!isShowingPerspective ? strings.addProduct(catalog) : strings.cannotAddProduct_Perspective(catalog)} ref={onSetRef}>
          <PrimaryIconButton
            {...props}
            tooltipStyle='mui'
            wrapperClassName={Classes.POPOVER2_DISMISS}
            disabled={isShowingPerspective}
          >
            {icons.add}
          </PrimaryIconButton>
        </Tooltip>
      </Popover2>
      <input ref={onSetAddCatalogItemInputRef} type='file' multiple accept={constants.VALID_IMAGE_EXTENSION.join(',')}
             onChange={onAddItemImageInputChanged} style={{display: 'none'}}/>
    </Fragment>
  )
})

const AddCatalogItemButton_old = observer<PrimaryIconButtonProps>(function AddCatalogItemButton(props) {
  const store = useVipLocalStore((s) => new AddCatalogItemButtonStore(s)),
    {hover} = store,
    {strings, catalog, catalog: {onSetRef, createItem: {onCreateItemFromImages, onAddItemImageInputChanged, onSetAddCatalogItemInputRef, onCreateItem, onCreateItems}, isShowingPerspective}, stores: {ui: {constants}}} = store

  return (
    <div className={css.addButton_old} data-type='AddCatalogItemButton' data-on-hover={hover}>
      <Card elevation={hover ? 1 : 0} onMouseEnter={() => store.hover = true}
            className={css.card}
            onMouseLeave={() => store.hover = false}>
        <Tooltip key='add-product' placement={'left'}
                 title={!isShowingPerspective ? strings.addProduct(catalog) : strings.cannotAddProduct_Perspective(catalog)} ref={onSetRef}>
          <PrimaryIconButton
            onClick={onCreateItem}
            {...props}
            disabled={isShowingPerspective}
          >
            {icons.add}
          </PrimaryIconButton>
        </Tooltip>
        <Tooltip key='add-products' placement={'left'}
                 className={css.addOfflineItem} data-display={hover}
                 title={!isShowingPerspective ? strings.addProducts(catalog) : strings.cannotAddProduct_Perspective(catalog)}>
          <PrimaryIconButton
            onClick={onCreateItems}
            {...props}
            disabled={isShowingPerspective}
          >
            {icons.addMany}
          </PrimaryIconButton>
        </Tooltip>
        {/* Removed in YFTS-8384
        {/*<Tooltip2 key='add-product-image' placement={'left'}*/}
        {/*         title={!isShowingPerspective ? strings.addProductWithImage : strings.cannotAddProduct_Perspective}*/}
        {/*          className={css.actionButton}*/}
        {/*>*/}
        {/*  <PrimaryIconButton*/}
        {/*    onClick={(e) => {*/}
        {/*      onCreateItemFromImages()*/}
        {/*    }}*/}
        {/*    {...props}*/}
        {/*    disabled={isShowingPerspective}*/}
        {/*  >*/}
        {/*    {icons.addFromImage}*/}
        {/*  </PrimaryIconButton>*/}
        {/*</Tooltip2>*/}
        <Tooltip key='add-products-images' placement={'left'}
                 className={css.addOfflineItem} data-display={hover}
                 title={`${!isShowingPerspective ? strings.addProductsWithImages(catalog) : strings.cannotAddProduct_Perspective(catalog)}...`}>
          <PrimaryIconButton
            onClick={(e) => {
              onCreateItemFromImages(true)
            }}
            {...props}
            disabled={isShowingPerspective}
          >
            {icons.addFromImages}
          </PrimaryIconButton>
        </Tooltip>
        <input ref={onSetAddCatalogItemInputRef} type='file' multiple accept={constants.VALID_IMAGE_EXTENSION.join(',')}
               onChange={onAddItemImageInputChanged} style={{display: 'none'}}/>
      </Card>
    </div>
  )
})

const SwitchCatalogViewDropdownButton = observer(function SwitchCatalogViewDropdownButton() {
  const {strings, groupBy: {hierarchyGroups}, groupBy, catalog, catalog: {isShowingAssortment, features: {actionButtons}, catalogConfig, toggleDataGridView, isProductView}} = useStores(),
    canSwitchToTableView = !isShowingAssortment && actionButtons?.tableView === true,
    canSwitchToGroupedCardView = !isProductView && hierarchyGroups.some(g => g?.esIndex),
    isFlatMode = groupBy.settings.groupView === 'flat'

  if (!canSwitchToGroupedCardView && !canSwitchToTableView) return null

  return <Fragment>
    {actionButtons?.tableView === true && <Fragment>
		<PrimaryIconButton
			tooltip={`${strings.tableView}`}
	  tooltipStyle='mui'
	  active={catalog.settings.viewMode === 'table'} icon={icons.table} onClick={toggleDataGridView}/>
	</Fragment>}
    {/*{canSwitchToGroupedCardView && <Tooltip2 content={strings.flatMode} placement='bottom'>*/}
    {/*  <PrimaryIconButton active={isFlatMode} icon={<Icon icon='alignment-vertical-center'/>} onClick={() => {*/}
    {/*    groupBy.settings.groupView = groupBy.settings.groupView === 'card' ? 'flat' : 'card'*/}
    {/*  }}/>*/}
    {/*</Tooltip2>}*/}
  </Fragment>

  /*
    return <Popover
      className={css.popupMenuContainer}
      popoverClassName={css.popupMenuPopover}
      position='bottom'
      interactionKind='hover'
      content={
        <div className={css.popupMenu}>
          {!isProductView && !isFlatMode && <Tooltip2 content={strings.flatMode} placement='left'>
        <PrimaryIconButton active={isFlatMode} icon={<Icon icon='alignment-vertical-center'/>} onClick={() => {
                settings.viewMode = 'card'
                hierarchyGroups.forEach(g => g.cardGroupedView = false)
              }}/>
      </Tooltip2>}

          {(isProductView ? viewMode === 'table' : !isGroupedCardView) && <Tooltip2 content={strings.cardView} placement='left'>
        <PrimaryIconButton active={isGroupedCardView} icon={<Icon icon='grid-view'/>} onClick={() => {
                settings.viewMode = 'card'
                hierarchyGroups.forEach(g => g.cardGroupedView = true)
              }}/>
      </Tooltip2>}
        </div>
      }>
      <PrimaryIconButton icon={<Icon icon={!isProductView && isFlatMode ? 'alignment-vertical-center' : viewMode === 'table' ? 'th' : 'grid-view'}/>}/>
    </Popover2>*/
})




// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/CatalogActionButtons.tsx